import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button, notification } from "antd";
import { httpService } from "../../../services/httpService.service";
import { extractName } from "../../../services/helpFunctions/extractNameOfFile";

/* Site-visit Excel exports are produced by a Hangfire job on the server: the
   POST only queues the work, and the finished workbook shows up later in the
   file manager. This hook owns that whole lifecycle for both scopes (whole
   project / selected cards) so the UI layer stays dumb, and so the boards stop
   carrying two copies of the same polling code.

   Deliberate differences from the previous implementation:
   - the poll is a self-scheduling timeout, not setInterval, so a slow response
     can never overlap the next request;
   - it is torn down on unmount and aborts its in-flight requests;
   - it only runs while an export is actually outstanding, not on every open;
   - one failed round no longer kills the whole watch;
   - it gives up after MAX_WATCH_MS instead of spinning forever. */

const baseUrl = process.env.REACT_APP_FILEMANAGER_API_BASE_URL;

export const EXPORT_SCOPES = {
  project: {
    folderName: "TPMSiteVisits",
    exportReason: "ProjectSiteVisitsExport",
    perUser: false,
  },
  user: {
    folderName: "TPM/TpmUserSiteVisits",
    exportReason: "UserSiteVisitsExport",
    perUser: true,
  },
};

// Backoff rather than a flat 7s: the first check is near-immediate so a small
// export feels instant, while long ones settle at 8s instead of hammering two
// APIs for minutes.
const FIRST_DELAY = 1200;
const MAX_DELAY = 8000;
const BACKOFF = 1.6;
const HIDDEN_DELAY = 5000;
const MAX_WATCH_MS = 5 * 60 * 1000;

// The POST returns before Hangfire has necessarily registered the job, and the
// file lands a moment after the job leaves Processing. So "no job running" only
// counts as an answer once the export is old enough, and twice in a row.
const NO_JOB_GRACE_MS = 20000;
const NO_JOB_MISSES = 2;

const ACTIVE_JOB_STATES = ["processing", "scheduled", "enqueued", "awaiting"];

const projectIdOf = () => localStorage.getItem("currentProjectID");
const userIdOf = () => localStorage.getItem("userID");

const isAbort = (err) =>
  err?.name === "CanceledError" ||
  err?.name === "AbortError" ||
  err?.code === "ERR_CANCELED";

// getExportFileByPlatform answers with a bare array, the job endpoints with
// { data: [...] }. Normalise both, and never trust either to be present.
const toList = (body) =>
  Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];

const isJobActive = (job) =>
  ACTIVE_JOB_STATES.includes(String(job?.stateName || "").toLowerCase());

const fileNameOf = (fileKey) => {
  if (!fileKey) return "Export.xlsx";
  const tail = String(fileKey).split("/").pop();
  return extractName(tail) || tail;
};

const normaliseFiles = (body) =>
  toList(body)
    .filter((f) => f?.fileKey)
    .map((f) => ({
      key: f.fileKey,
      name: fileNameOf(f.fileKey),
      createdDate: f.createdDate || null,
      url: f.url || null,
    }))
    .sort(
      (a, b) =>
        new Date(b.createdDate || 0).getTime() -
        new Date(a.createdDate || 0).getTime()
    );

// An anchor click inside the user's own click handler, instead of window.open:
// pop-up blockers leave it alone and the browser saves the workbook rather than
// flashing an empty tab at a presigned URL.
export const downloadExportFile = (file) => {
  if (!file?.url) return;
  const link = document.createElement("a");
  link.href = file.url;
  link.download = file.name || "Export.xlsx";
  link.rel = "noopener";
  link.target = "_blank";
  document.body.appendChild(link);
  link.click();
  link.remove();
};

let jobSeq = 0;

export const useExportJobs = ({ scope = "project", open = false }) => {
  const cfg = EXPORT_SCOPES[scope] || EXPORT_SCOPES.project;

  const [files, setFiles] = useState([]);
  const [pending, setPending] = useState([]);
  const [stalled, setStalled] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [listError, setListError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | watching | timedOut
  const [lastChecked, setLastChecked] = useState(null);

  // The watch loop reads these from its own closure, so they have to be current
  // the moment a round finishes rather than after the next commit.
  const filesRef = useRef(files);
  const pendingRef = useRef(pending);
  useEffect(() => {
    filesRef.current = files;
  }, [files]);
  useEffect(() => {
    pendingRef.current = pending;
  }, [pending]);

  const fetchFiles = useCallback(
    async (signal) => {
      const params = { entityId: projectIdOf(), folderName: cfg.folderName };
      if (cfg.perUser) params.userId = userIdOf();
      const res = await axios.get(`${baseUrl}FileData/getExportFileByPlatform`, {
        params,
        signal,
      });
      return normaliseFiles(res?.data);
    },
    [cfg]
  );

  // Both job endpoints are consulted, but with allSettled: if one is down or
  // changes shape we still get an answer from the other instead of tearing the
  // watch down. exportReason goes through axios params so it is encoded once,
  // without the stray literal quotes the old URL carried.
  const probeActiveJobs = useCallback(
    async (signal) => {
      const projectId = projectIdOf();
      const userId = userIdOf();

      let hangfireUrl = `api/Hangfire/getProjectJobs?projectId=${projectId}`;
      if (cfg.perUser) hangfireUrl += `&userId=${userId}`;

      const fileParams = {
        entityId: projectId,
        exportReason: cfg.exportReason,
      };
      if (cfg.perUser) fileParams.userId = userId;

      const rounds = await Promise.allSettled([
        new Promise((resolve, reject) =>
          httpService.get(hangfireUrl, resolve, reject)
        ),
        axios
          .get(`${baseUrl}FileData/getEntityJobs`, {
            params: fileParams,
            signal,
          })
          .then((res) => res?.data),
      ]);

      const bodies = rounds
        .filter((r) => r.status === "fulfilled")
        .map((r) => r.value);

      if (!bodies.length) return { reachable: false, active: false };
      return {
        reachable: true,
        active: bodies.some((body) => toList(body).some(isJobActive)),
      };
    },
    [cfg]
  );

  const refresh = useCallback(async () => {
    setLoadingFiles(true);
    try {
      const list = await fetchFiles();
      setFiles(list);
      filesRef.current = list;
      setListError(null);
      setLastChecked(Date.now());
      return list;
    } catch (err) {
      if (!isAbort(err)) {
        setListError("We couldn't load your export files. Try again.");
      }
      return null;
    } finally {
      setLoadingFiles(false);
    }
  }, [fetchFiles]);

  const notifyReady = useCallback(
    (file) => {
      const key = `export-ready-${scope}-${file.key}`;
      notification.success({
        key,
        message: "Export ready",
        description: file.name,
        duration: 0,
        btn: (
          <Button
            type="primary"
            size="small"
            onClick={() => {
              downloadExportFile(file);
              notification.destroy(key);
            }}
          >
            Download
          </Button>
        ),
      });
    },
    [scope]
  );

  // No job id comes back from the POST and the file list carries no job
  // reference, so arrivals are matched to waiting exports in order. Good enough
  // in practice, and honest about what the API actually tells us.
  const settleArrivals = useCallback(
    (arrived) => {
      const waiting = pendingRef.current;
      const consumed = Math.min(arrived.length, waiting.length);
      if (!consumed) return;
      const rest = waiting.slice(consumed);
      pendingRef.current = rest;
      setPending(rest);
      arrived.slice(0, consumed).forEach(notifyReady);
    },
    [notifyReady]
  );

  const markStalled = useCallback(() => {
    const stuck = pendingRef.current;
    if (!stuck.length) return;
    pendingRef.current = [];
    setPending([]);
    setStalled((prev) => [
      ...prev,
      ...stuck.map((job) => ({ ...job, state: "stalled" })),
    ]);
    notification.warning({
      message: "Export not confirmed",
      description:
        "The server reports no export running and no new file arrived. Please start the export again.",
      duration: 8,
    });
  }, []);

  const queueWatch = useCallback((entry) => {
    setPending((prev) => {
      const next = [...prev, entry];
      pendingRef.current = next;
      return next;
    });
    setPhase("watching");
  }, []);

  const submit = useCallback(
    async ({ includeFiles = false, siteVisitIds } = {}) => {
      setSubmitting(true);
      setStalled([]);

      /* Seed the baseline before queueing anything. The watch loop decides a
         file is "new" by comparing against what it knew when the watch began,
         and an export started from a surface that never opened the panel — the
         preview modal's single-card Excel button — starts from an empty list.
         Without this it would read the whole folder as new and hand someone
         else's old workbook back as yours. One cheap request avoids that. */
      try {
        const list = await fetchFiles();
        setFiles(list);
        filesRef.current = list;
      } catch (err) {
        // A failed baseline costs precision, not the export.
      }

      return new Promise((resolve) => {
        const payload = {
          projectId: projectIdOf(),
          includeFiles: !!includeFiles,
        };
        if (siteVisitIds?.length) payload.siteVisitIds = siteVisitIds;

        httpService.post(
          "/api/Site/exportSiteVisits",
          payload,
          () => {
            jobSeq += 1;
            queueWatch({
              id: `job-${Date.now()}-${jobSeq}`,
              startedAt: Date.now(),
              includeFiles: !!includeFiles,
              count: siteVisitIds?.length || null,
              state: "generating",
            });
            setSubmitting(false);
            notification.info({
              key: `export-queued-${scope}`,
              message: "Export queued",
              description:
                "The workbook is being generated on the server. You can close this window — we'll tell you when it's ready.",
              duration: 5,
            });
            resolve({ ok: true });
          },
          (error) => {
            setSubmitting(false);
            const text =
              error?.errors?.[0]?.message ||
              error?.message ||
              "We couldn't start the export. Please try again.";
            notification.error({
              message: "Export failed to start",
              description: text,
              duration: 6,
            });
            resolve({ ok: false, error: text });
          }
        );
      });
    },
    [queueWatch, scope, fetchFiles]
  );

  const retry = useCallback(() => {
    if (!pendingRef.current.length) return;
    setPhase("watching");
  }, []);

  const dismissStalled = useCallback(() => setStalled([]), []);

  /* The watch loop. Runs only while phase === "watching", i.e. only while at
     least one export is outstanding, and keeps running after the modal closes,
     which is what makes the notification worth anything. */
  useEffect(() => {
    if (phase !== "watching") return undefined;

    const controller = new AbortController();
    const openedAt = Date.now();
    const known = new Set(filesRef.current.map((f) => f.key));

    let cancelled = false;
    let timer = null;
    let delay = FIRST_DELAY;
    let misses = 0;

    const stop = (next) => {
      cancelled = true;
      clearTimeout(timer);
      setPhase(next);
    };

    const tick = async () => {
      if (cancelled) return;

      // Nothing is going to change while nobody is looking; a background tab
      // just re-checks cheaply until it comes back.
      if (typeof document !== "undefined" && document.hidden) {
        timer = setTimeout(tick, HIDDEN_DELAY);
        return;
      }

      try {
        const probe = await probeActiveJobs(controller.signal);
        if (cancelled) return;

        const list = await fetchFiles(controller.signal);
        if (cancelled) return;

        setFiles(list);
        filesRef.current = list;
        setLastChecked(Date.now());

        const arrived = list.filter((f) => !known.has(f.key));
        arrived.forEach((f) => known.add(f.key));
        if (arrived.length) settleArrivals(arrived);

        if (!pendingRef.current.length) {
          stop("idle");
          return;
        }

        const oldest = pendingRef.current[0];
        const settledLongEnough =
          Date.now() - oldest.startedAt > NO_JOB_GRACE_MS;

        if (!probe.active && probe.reachable && settledLongEnough) {
          misses += 1;
          if (misses >= NO_JOB_MISSES) {
            markStalled();
            stop("idle");
            return;
          }
        } else {
          misses = 0;
        }
      } catch (err) {
        if (cancelled || isAbort(err)) return;
        // One bad round is not a reason to stop watching.
      }

      if (Date.now() - openedAt > MAX_WATCH_MS) {
        stop("timedOut");
        return;
      }

      delay = Math.min(Math.round(delay * BACKOFF), MAX_DELAY);
      timer = setTimeout(tick, delay);
    };

    tick();

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [phase, fetchFiles, probeActiveJobs, settleArrivals, markStalled]);

  /* On open: load the list once. Then, if the user reloaded the page or came
     back later while a job is still running, pick that job back up so the panel
     shows a live row instead of a stale list. */
  useEffect(() => {
    if (!open) return undefined;

    const controller = new AbortController();
    let alive = true;

    (async () => {
      setLoadingFiles(true);
      try {
        const list = await fetchFiles(controller.signal);
        if (!alive) return;
        setFiles(list);
        filesRef.current = list;
        setListError(null);
        setLastChecked(Date.now());
      } catch (err) {
        if (alive && !isAbort(err)) {
          setListError("We couldn't load your export files. Try again.");
        }
      } finally {
        if (alive) setLoadingFiles(false);
      }

      if (!alive || pendingRef.current.length) return;
      try {
        const probe = await probeActiveJobs(controller.signal);
        if (!alive || !probe.active) return;
        queueWatch({
          id: `resumed-${Date.now()}`,
          startedAt: Date.now(),
          includeFiles: null,
          count: null,
          state: "generating",
          resumed: true,
        });
      } catch (err) {
        // Nothing to resume.
      }
    })();

    return () => {
      alive = false;
      controller.abort();
    };
  }, [open, fetchFiles, probeActiveJobs, queueWatch]);

  return {
    files,
    pending,
    stalled,
    loadingFiles,
    listError,
    submitting,
    phase,
    isWatching: phase === "watching",
    lastChecked,
    submit,
    refresh,
    retry,
    dismissStalled,
  };
};

export default useExportJobs;
